import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface Accion {
  etiqueta: string;
  onClick: () => void;
  /** Rojo y al final: separa lo que se deshace de lo que no. */
  destructiva?: boolean;
  testId?: string;
}

/** Alto que se supone para cada opción antes de poder medir el panel. */
const ALTO_OPCION = 48;

/**
 * El menú de tres puntos.
 *
 * Contiene **acciones sobre el registro** —verlo, editarlo, duplicarlo,
 * eliminarlo— y nada más. Los cambios de estado viven en otro lado, como
 * botones explícitos. Esa separación es el criterio: acá está lo que le pasa al
 * trabajo *como registro*; allá, lo que pasa con la plata.
 *
 * Las acciones destructivas van **al final y en rojo**. No es decoración: en un
 * menú corto, la última posición es la más difícil de tocar por error cuando el
 * dedo va rápido buscando la primera.
 */
export function MenuAcciones({
  acciones,
  etiqueta = 'Acciones',
  testId,
  onAbrir,
}: {
  acciones: Accion[];
  etiqueta?: string;
  testId?: string;
  /** Se avisa al abrir, para que quien contenga el menú pueda apartarse. */
  onAbrir?: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [haciaArriba, setHaciaArriba] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Tocar afuera cierra. Sin esto, en un teléfono el menú queda abierto
  // tapando la fila de al lado y hay que buscar dónde tocar para salir.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: PointerEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('pointerdown', fuera);
    return () => document.removeEventListener('pointerdown', fuera);
  }, [abierto]);

  /**
   * De qué lado abrirlo.
   *
   * Un menú en la última tarjeta de la lista se dibujaba fuera de la pantalla:
   * las opciones existían pero no había forma de tocarlas, y la de abajo de
   * todo —la destructiva— era la que quedaba siempre cortada.
   *
   * Se mide después de pintar y antes de que el navegador lo muestre, así no se
   * ve el salto. Si no hay lugar abajo pero sí arriba, se da vuelta; si no hay
   * lugar de ningún lado, se queda abajo y se desplaza solo, que es preferible
   * a taparle a alguien lo que estaba mirando.
   */
  useLayoutEffect(() => {
    if (!abierto) return;
    const r = boton.current?.getBoundingClientRect();
    if (!r) return;
    const alto = panel.current?.offsetHeight || acciones.length * ALTO_OPCION;
    const abajo = window.innerHeight - r.bottom;
    setHaciaArriba(abajo < alto && r.top > abajo);
  }, [abierto, acciones.length]);

  return (
    <div className="relative shrink-0" ref={caja}>
      <button
        ref={boton}
        type="button"
        onClick={() => {
          setAbierto((v) => {
            if (!v) onAbrir?.();
            return !v;
          });
        }}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={etiqueta}
        className="flex min-h-11 w-10 items-center justify-center rounded-lg text-lg opacity-70"
        data-testid={testId}
      >
        ⋮
      </button>

      {abierto && (
        <div
          ref={panel}
          role="menu"
          className={`absolute right-0 z-30 min-w-44 overflow-hidden superficie bg-[var(--superficie)] shadow-lg ${
            haciaArriba ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          data-testid={testId ? `${testId}-panel` : undefined}
          data-hacia={haciaArriba ? 'arriba' : 'abajo'}
        >
          {acciones.map((a) => (
            <button
              key={a.etiqueta}
              type="button"
              role="menuitem"
              onClick={() => {
                setAbierto(false);
                a.onClick();
              }}
              className={`flex min-h-12 w-full items-center px-4 text-left text-sm ${
                a.destructiva ? 'border-t border-[var(--linea)] text-[var(--peligro)] ' : ''
              }`}
              data-testid={a.testId}
            >
              {a.etiqueta}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
